import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { isSupabaseInitialized } from "@/integrations/supabase/client";

interface ErrorPageProps {
  error?: Error;
  componentStack?: string | null;
  resetErrorBoundary?: () => void;
  statusCode?: number;
}

const ErrorPage = ({ 
  error, 
  componentStack,
  resetErrorBoundary,
  statusCode = 500 
}: ErrorPageProps) => {
  // Safely use React Router hooks with a check
  let navigate;
  let location;
  
  // Check if we're in a router context
  const inRouterContext = () => {
    try {
      // Try to use the hooks - will throw if not in Router context
      navigate = useNavigate();
      location = useLocation();
      return true;
    } catch (e) {
      console.warn("ErrorPage rendered outside Router context");
      return false;
    }
  };
  
  const hasRouter = inRouterContext();
  
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [troubleshootingTips, setTroubleshootingTips] = useState<string[]>([]);
  const [componentInfo, setComponentInfo] = useState<string | null>(null);
  
  // Safely get URL parameters if we have a router context
  const errorType = hasRouter && location ? new URLSearchParams(location.search).get('type') : null;
  
  useEffect(() => {
    if (error) {
      console.error("Application error:", error);
      
      // Extract component info from stack if available
      if (componentStack) {
        try {
          const firstLine = componentStack.split('\n')[1];
          if (firstLine) {
            const component = firstLine.trim().replace(/^at\s+/, '');
            setComponentInfo(component);
            console.log("Error occurred in component:", component);
          }
        } catch (e) {
          console.error("Failed to parse component stack:", e);
        }
      }
      
      // Check if we're in development mode using a more secure approach
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        setErrorDetails(error.message || "Unknown error (empty error message)");
      } else {
        // In production, show a simplified error message that doesn't expose internals
        setErrorDetails(getSimplifiedErrorMessage(error));
      }
      
      // Add specific troubleshooting tips based on error type
      setTroubleshootingTips(getTroubleshootingTips(error, componentStack));
    } else {
      setErrorDetails("Unknown error occurred (no error object provided)");
    }
  }, [error, componentStack]);
  
  // Generate user-friendly error message
  const getSimplifiedErrorMessage = (error: Error): string => {
    // Special case for empty error messages
    if (!error.message) {
      return "An error occurred with no message. This is often caused by a rendering issue in a component.";
    }
    
    const msg = error.message.toLowerCase();
    
    if (msg.includes('supabase') || msg.includes('database') || msg.includes('connection')) {
      return "Unable to connect to our database services. This could be a temporary issue.";
    } else if (msg.includes('network') || msg.includes('fetch') || msg.includes('api')) {
      return "Network connection error. Please check your internet connection.";
    } else if (msg.includes('auth') || msg.includes('login') || msg.includes('permission')) {
      return "Authentication error. Please try logging in again.";
    } else {
      return "An unexpected error occurred. Our team has been notified.";
    }
  };
  
  // Get troubleshooting tips based on error type
  const getTroubleshootingTips = (error: Error, componentStack?: string | null): string[] => {
    const tips: string[] = [
      "Check your network connection",
      "Try refreshing the page",
      "Contact support if the problem persists"
    ];
    
    // Empty error message usually indicates a rendering issue
    if (!error.message) {
      tips.push(
        "Try clearing your browser cache",
        "Disable browser extensions that might interfere with the app",
        "Try using a different browser"
      );
      return tips;
    }
    
    const msg = error.message.toLowerCase();
    
    // Database connection issues
    if (msg.includes('supabase') || msg.includes('database') || !isSupabaseInitialized()) {
      tips.push(
        "Clear your browser cache",
        "Try using a different browser",
        "Disable any browser extensions that might block connections"
      );
    }
    
    // Auth issues
    if (msg.includes('auth') || msg.includes('login') || msg.includes('permission')) {
      tips.push(
        "Try logging out and back in",
        "Make sure cookies are enabled in your browser",
        "Check if you're using the correct account"
      );
    }
    
    // Loading/module issues
    if (msg.includes('module') || msg.includes('import') || msg.includes('component')) {
      tips.push(
        "Clear your browser cache",
        "Try a hard refresh (Ctrl+F5 or Cmd+Shift+R)",
        "Update your browser to the latest version"
      );
    }
    
    return tips;
  };

  const handleGoHome = () => {
    if (hasRouter) {
      navigate("/");
    } else {
      window.location.href = "/";
    }
  };

  const handleRetry = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      // If no reset function is provided, just refresh the page
      window.location.reload();
    }
  };
  
  const handleClearCache = () => {
    // Clear localStorage
    localStorage.clear();
    // Clear sessionStorage
    sessionStorage.clear();
    // Reload the page
    window.location.reload();
  };

  // Render specific content for configuration errors
  if (errorType === 'config') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h1 className="text-4xl font-extrabold text-amber-500 mb-4">Configuration Error</h1>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Missing Configuration</h2>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 text-left">
              <p className="text-sm text-amber-700">
                The application cannot start because it's missing required configuration.
                Please ensure that the application is properly configured.
              </p>
            </div>
            
            <div className="text-left text-sm text-gray-600 mt-4">
              <h3 className="font-semibold mb-2">How to fix this:</h3>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Check if you're using the latest version of the application</li>
                <li>Try clearing your browser cache and reloading</li>
                <li>Contact support if the issue persists</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Only include Helmet if we're in a proper React Router context */}
      {hasRouter && (
        <Helmet>
          <title>Error | BackLink Hub</title>
        </Helmet>
      )}
      
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h1 className="text-6xl font-extrabold text-red-500 mb-2">{statusCode}</h1>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {statusCode === 500 ? "Server Error" : "Something went wrong"}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              We're sorry, but we encountered an unexpected error.
            </p>
            
            {errorDetails && (
              <Alert variant="destructive" className="mb-6 text-left">
                <AlertTitle>Error details</AlertTitle>
                <AlertDescription className="mt-2 font-mono text-sm break-words">
                  {errorDetails}
                </AlertDescription>
              </Alert>
            )}
            
            {componentInfo && (
              <Alert className="mb-6 text-left bg-blue-50">
                <AlertTitle>Error location</AlertTitle>
                <AlertDescription className="mt-2 font-mono text-sm break-words">
                  Error occurred in component: {componentInfo}
                </AlertDescription>
              </Alert>
            )}
            
            {troubleshootingTips.length > 0 && (
              <div className="mb-6 text-left bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-700 mb-2">Try these troubleshooting steps:</h3>
                <ul className="list-disc pl-5 space-y-1 text-blue-900">
                  {troubleshootingTips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button onClick={handleRetry} variant="default">
                Try Again
              </Button>
              <Button onClick={handleGoHome} variant="outline">
                Return Home
              </Button>
              <Button onClick={handleClearCache} variant="outline" className="mt-2 sm:mt-0">
                Clear Cache & Reload
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ErrorPage;
